import { toRaw } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import {
  getBetslipLogDetails,
  useBetslipNotificationEvents
} from '@nsftx/seven-client-retail-vue-sgp'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import type { PrintContextPayinSlip } from '@nsftx/seven-client-retail-print'
import { TicketResolveError } from '@nsftx/seven-client-retail-vue-sgp'
import printService from '@/modules/print/services/printService'
import i18n from '@/plugins/translationsPlugin'
import errorParser from '@/utils/errorParser'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { TICKET_ACTION_PENDING_NOTIFICATION_ID } from '@/modules/ticket-checker/ticketCheckerService'

const LOG_PREFIX = '[ticketPayinService]'
const { t } = i18n.global

const initTicketPayin = () => {
  const { registerBetslipEventHandlers } = useBetslipNotificationEvents()

  registerBetslipEventHandlers({
    onConfirmPlaceFinished: handleTicketPayinSuccess
  })
}

const handleTicketPayinSuccess = async (betslip: BetslipView) => {
  const { showNotification, closeNotificationById } = useNotificationsStore()

  logService.debug(`${LOG_PREFIX} Ticket pay-in accepted.`, {
    code: 'ST_TICKET_PAYIN_ACCEPTED',
    ...getBetslipLogDetails(betslip)
  })
  closeNotificationById(TICKET_ACTION_PENDING_NOTIFICATION_ID)

  try {
    await printTicket(betslip)
    logService.info(`${LOG_PREFIX} Ticket successfully printed.`, {
      code: 'ST_TICKET_PAYIN_PRINT_SUCCESS',
      ...getBetslipLogDetails(betslip)
    })
    showNotification({
      variant: 'success',
      text: [t('ticket_payin_success')]
    })
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to print ticket.`, {
      code: 'ST_TICKET_PAYIN_PRINT_ERROR',
      ...errorParser.parseUpstream(error),
      ...getBetslipLogDetails(betslip)
    })
    showNotification({
      variant: 'error',
      text: [t('ticket_print_failed')]
    })
  }
}

const handleTicketPayinError = (error: unknown) => {
  logService.error(`${LOG_PREFIX} Ticket pay-in has failed.`, {
    code: 'ST_TICKET_PAYIN_FAILED',
    ...errorParser.parseUpstream(error),
    ticket_code: error instanceof TicketResolveError ? error.options.context.betslipId : null,
    feBetslipId: error instanceof TicketResolveError ? error.options.context.feBetslipId : null,
    details: error instanceof TicketResolveError ? toRaw(error.options.context.errorDetails) : null
  })
}

const printTicket = async (betslip: BetslipView) => {
  const linkCode = betslip.context.linkCode
  const printContext: PrintContextPayinSlip = {
    meta: {
      requestId: uuidv4(),
      productDisplayId: linkCode.toString()
    },
    model: {
      slip: betslip
    }
  }

  logService.debug(`${LOG_PREFIX} Sending template context data to ticket print`, {
    code: 'ST_TICKET_PAYIN_PRINT_TEMPLATE_CONTEXT_SUCCESS',
    details: {
      meta: printContext.meta,
      model: {
        slip: toRaw(betslip)
      }
    }
  })

  await printService
    .getPrintService()
    .performPrint(`games${linkCode}`, 'betslip_payin', printContext)
}

export { initTicketPayin, handleTicketPayinSuccess, handleTicketPayinError }
